import { useEffect } from "react";

/**
 * Lightweight, dependency-free SEO helper. Mounts <title> + meta tags into
 * document.head on mount and cleans up the additions on unmount. Designed to
 * replace the need for react-helmet-async for this project's flat routing.
 *
 * Usage:
 *   <Seo
 *     title="Page Title | Elizaveta von Stuben"
 *     description="150-160 character meta description for this page."
 *     canonical="https://vonstuben.com/route"
 *   />
 *
 * Notes:
 * - Only one <title> ever exists in the document; we overwrite it on mount and
 *   restore the previous title on unmount (covers SPA navigation correctly).
 * - Meta tags are upserted by `name=` selector; if a tag already exists in
 *   index.html (e.g. the global og: defaults), its content is updated rather
 *   than duplicated.
 */
function upsertMeta({ name, property, content }) {
    if (!content) return null;
    const selector = name
        ? `meta[name="${name}"]`
        : `meta[property="${property}"]`;
    let el = document.head.querySelector(selector);
    let created = false;
    if (!el) {
        el = document.createElement("meta");
        if (name) el.setAttribute("name", name);
        if (property) el.setAttribute("property", property);
        document.head.appendChild(el);
        created = true;
    }
    const prev = el.getAttribute("content");
    el.setAttribute("content", content);
    return { el, prev, created };
}

function upsertLink({ rel, href }) {
    if (!href) return null;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    let created = false;
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created = true;
    }
    const prev = el.getAttribute("href");
    el.setAttribute("href", href);
    return { el, prev, created };
}

export default function Seo({
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogType = "website",
    twitterCard = "summary_large_image",
    // Phase 12 — optional robots directive. Defaults to undefined so
    // existing pages (which never passed this prop) keep their previous
    // behavior of relying on the crawler's implicit `index, follow`
    // default. Pages that want to be explicit can pass e.g.
    // `robots: "index, follow"` (campaign landing pages) or
    // `robots: "noindex, follow"` (utility pages).
    robots,
}) {
    useEffect(() => {
        // ----- <title> -----
        const prevTitle = document.title;
        if (title) document.title = title;

        // ----- meta tags -----
        const refs = [];
        const push = (r) => r && refs.push(r);

        push(upsertMeta({ name: "description", content: description }));

        // Phase 12 — optional robots directive
        push(upsertMeta({ name: "robots", content: robots }));

        // Open Graph
        push(upsertMeta({ property: "og:title", content: ogTitle || title }));
        push(upsertMeta({ property: "og:description", content: ogDescription || description }));
        push(upsertMeta({ property: "og:type", content: ogType }));
        push(upsertMeta({ property: "og:url", content: canonical || (typeof window !== "undefined" ? window.location.href : undefined) }));
        push(upsertMeta({ property: "og:image", content: ogImage }));

        // Twitter
        push(upsertMeta({ name: "twitter:card", content: twitterCard }));
        push(upsertMeta({ name: "twitter:title", content: ogTitle || title }));
        push(upsertMeta({ name: "twitter:description", content: ogDescription || description }));
        push(upsertMeta({ name: "twitter:image", content: ogImage }));

        // Canonical
        push(upsertLink({ rel: "canonical", href: canonical }));

        return () => {
            // Restore previous values so leaving the page doesn't strand stale tags.
            document.title = prevTitle;
            refs.forEach((r) => {
                if (!r) return;
                if (r.created) {
                    r.el.remove();
                } else if (r.prev !== null && r.prev !== undefined) {
                    r.el.setAttribute("content", r.prev);
                }
            });
        };
    }, [title, description, canonical, ogTitle, ogDescription, ogImage, ogType, twitterCard, robots]);

    return null;
}
